import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@heroui/react";
import R1FilterBar from "../components/R1FilterBar";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import { MOST_RECENT } from "../constants";
import {
  buildFundScopeParams,
  buildR1DownloadUrl,
  fundSelectionKey,
  r1Api,
  type R1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";
import { ScrollableX } from "../components/ScrollableX";

function buildCapitalAccountFilteredUrl(
  selection: R1FundSelection,
  period: string | null,
): string {
  const params = buildFundScopeParams(selection);
  if (period && period !== MOST_RECENT) {
    params.append("reporting_date_equals", period);
  } else {
    params.set("most_recent", "true");
  }
  return buildR1DownloadUrl("r1_capital_account", params);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type RawCA = Record<string, unknown>;

function toTimestamp(value: unknown): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const timestamp = Date.parse(String(value));
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

function compareCapitalAccountRows(a: RawCA, b: RawCA): number {
  // reporting_date is a YYYYMM integer — sort numerically descending
  const rdCmp = Number(b.reporting_date ?? 0) - Number(a.reporting_date ?? 0);
  if (rdCmp !== 0) return rdCmp;

  const createdAtCmp = toTimestamp(b.created_at) - toTimestamp(a.created_at);
  if (createdAtCmp !== 0) return createdAtCmp;

  return Number(b.id ?? 0) - Number(a.id ?? 0);
}

function fmtNum(row: RawCA, key: string, isPct = false): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v).trim();
  if (s === "") return "—";
  const n = Number(s);
  if (isNaN(n) || String(n) !== s.replace(/\.0+$/, "")) return s;
  if (n === 0) return "0";
  if (isPct) return (n * 100).toFixed(2) + "%";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

function fmtText(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  return String(v).trim();
}

function fmtMult(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return n.toFixed(2) + "x";
}

function fmtBool(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined) return "—";
  return v ? "Yes" : "No";
}

// ─── Row config ──────────────────────────────────────────────────────────────

type RowCfg =
  | { type: "section"; label: string }
  | {
      type: "data";
      label: string;
      tf: string;
      r1: string;
      isPct?: boolean;
      isMult?: boolean;
      isBool?: boolean;
      isText?: boolean;
      isSingle?: boolean;
    };

const ROW_CONFIG: RowCfg[] = [
  { type: "section", label: "General Information" },
  {
    type: "data",
    label: "Fund Name",
    tf: "gi_fund_name_val",
    r1: "gi_fund_name_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "LP Share (%)",
    tf: "gi_share_pct_val",
    r1: "gi_share_pct_val",
    isPct: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Reporting Date",
    tf: "gi_reporting_date_val",
    r1: "gi_reporting_date_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Reporting Currency",
    tf: "gi_reporting_currency_val",
    r1: "gi_reporting_currency_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Tax ID",
    tf: "gi_tax_id_val",
    r1: "gi_tax_id_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Transparency Register",
    tf: "gi_transparency_register_val",
    r1: "gi_transparency_register_val",
    isBool: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Fund Status",
    tf: "gi_fund_status_val",
    r1: "gi_fund_status_val",
    isText: true,
    isSingle: true,
  },

  { type: "section", label: "Since Inception" },
  {
    type: "data",
    label: "Total Commitment",
    tf: "si_total_commitment_tf",
    r1: "si_total_commitment_r1",
  },
  {
    type: "data",
    label: "Unfunded Commitment",
    tf: "si_unfunded_commitment_tf",
    r1: "si_unfunded_commitment_r1",
  },
  {
    type: "data",
    label: "Paid in Capital (Total)",
    tf: "si_paid_in_capital_tf",
    r1: "si_paid_in_capital_r1",
  },
  {
    type: "data",
    label: "Distributions (Total)",
    tf: "si_distributions_tf",
    r1: "si_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Return of Capital",
    tf: "si_thereof_return_of_capital_tf",
    r1: "si_thereof_return_of_capital_r1",
  },
  {
    type: "data",
    label: "  thereof Gain/Loss",
    tf: "si_thereof_gain_loss_tf",
    r1: "si_thereof_gain_loss_r1",
  },
  {
    type: "data",
    label: "Recallable Distributions",
    tf: "si_recallable_distributions_tf",
    r1: "si_recallable_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Already Recalled",
    tf: "si_thereof_already_recalled_tf",
    r1: "si_thereof_already_recalled_r1",
  },
  {
    type: "data",
    label: "  thereof Current Recallable",
    tf: "si_thereof_current_recallable_tf",
    r1: "si_thereof_current_recallable_r1",
  },
  {
    type: "data",
    label: "Realized Gain/Losses",
    tf: "si_realized_gain_losses_tf",
    r1: "si_realized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Unrealized Gain/Losses",
    tf: "si_unrealized_gain_losses_tf",
    r1: "si_unrealized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Operating Income/Expenses",
    tf: "si_operating_income_expenses_tf",
    r1: "si_operating_income_expenses_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid by LP",
    tf: "si_equalisation_paid_by_lp_tf",
    r1: "si_equalisation_paid_by_lp_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid to LP",
    tf: "si_equalisation_paid_to_lp_tf",
    r1: "si_equalisation_paid_to_lp_r1",
  },
  {
    type: "data",
    label: "Management Fees Paid",
    tf: "si_management_fees_paid_tf",
    r1: "si_management_fees_paid_r1",
  },
  {
    type: "data",
    label: "NAV before Carry",
    tf: "si_nav_before_carry_tf",
    r1: "si_nav_before_carry_r1",
  },
  {
    type: "data",
    label: "Carried Interest Accrued",
    tf: "si_carried_interest_accrued_tf",
    r1: "si_carried_interest_accrued_r1",
  },
  {
    type: "data",
    label: "Carried Interest Paid",
    tf: "si_carried_interest_paid_tf",
    r1: "si_carried_interest_paid_r1",
  },
  {
    type: "data",
    label: "NAV after Carry",
    tf: "si_nav_after_carry_tf",
    r1: "si_nav_after_carry_r1",
  },
  {
    type: "data",
    label: "TVPI",
    tf: "si_tvpi_tf",
    r1: "si_tvpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "DPI",
    tf: "si_dpi_tf",
    r1: "si_dpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "RVPI",
    tf: "si_rvpi_tf",
    r1: "si_rvpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "Gross IRR",
    tf: "si_gross_irr_tf",
    r1: "si_gross_irr_r1",
    isPct: true,
  },
  {
    type: "data",
    label: "Net IRR",
    tf: "si_net_irr_tf",
    r1: "si_net_irr_r1",
    isPct: true,
  },

  { type: "section", label: "Current Quarter" },
  {
    type: "data",
    label: "Paid in Capital",
    tf: "cq_paid_in_capital_tf",
    r1: "cq_paid_in_capital_r1",
  },
  {
    type: "data",
    label: "Distributions",
    tf: "cq_distributions_tf",
    r1: "cq_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Return of Capital",
    tf: "cq_thereof_return_of_capital_tf",
    r1: "cq_thereof_return_of_capital_r1",
  },
  {
    type: "data",
    label: "  thereof Gain/Loss",
    tf: "cq_thereof_gain_loss_tf",
    r1: "cq_thereof_gain_loss_r1",
  },
  {
    type: "data",
    label: "Recallable Distributions",
    tf: "cq_recallable_distributions_tf",
    r1: "cq_recallable_distributions_r1",
  },
  {
    type: "data",
    label: "Realized Gain/Losses",
    tf: "cq_realized_gain_losses_tf",
    r1: "cq_realized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Operating Income/Expenses",
    tf: "cq_operating_income_expenses_tf",
    r1: "cq_operating_income_expenses_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid by r1",
    tf: "cq_equalisation_paid_by_r1_tf",
    r1: "cq_equalisation_paid_by_r1_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid to r1",
    tf: "cq_equalisation_paid_to_r1_tf",
    r1: "cq_equalisation_paid_to_r1_r1",
  },
  {
    type: "data",
    label: "Management Fees Paid",
    tf: "cq_management_fees_paid_tf",
    r1: "cq_management_fees_paid_r1",
  },
  {
    type: "data",
    label: "NAV before Carry",
    tf: "cq_nav_before_carry_tf",
    r1: "cq_nav_before_carry_r1",
  },
  {
    type: "data",
    label: "Carried Interest Accrued",
    tf: "cq_carried_interest_accrued_tf",
    r1: "cq_carried_interest_accrued_r1",
  },
  {
    type: "data",
    label: "Carried Interest Paid",
    tf: "cq_carried_interest_paid_tf",
    r1: "cq_carried_interest_paid_r1",
  },
  {
    type: "data",
    label: "NAV after Carry",
    tf: "cq_nav_after_carry_tf",
    r1: "cq_nav_after_carry_r1",
  },
];

// ─── Value resolver ───────────────────────────────────────────────────────────

function resolveValue(
  raw: RawCA,
  cfg: Extract<RowCfg, { type: "data" }>,
  key: "tf" | "r1",
): string {
  const field = cfg[key];
  if (cfg.isText) return fmtText(raw, field);
  if (cfg.isMult) return fmtMult(raw, field);
  if (cfg.isBool) return fmtBool(raw, field);
  return fmtNum(raw, field, cfg.isPct);
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function R1CapitalAccountView() {
  const { selectedFundIds, selectedPeriod, funds, fundSelection } =
    useR1DashboardContext();
  const isMostRecent = selectedPeriod === MOST_RECENT;
  const apiPeriod = isMostRecent ? null : selectedPeriod;
  const scopeKey = fundSelectionKey(fundSelection);

  const { data: batchData, isLoading } = useQuery({
    queryKey: ["r1", "capital-account-batch", scopeKey, apiPeriod],
    queryFn: () => r1Api.getCapitalAccountBatch(fundSelection, apiPeriod),
    enabled: selectedFundIds.length > 0,
    staleTime: 30_000,
  });

  const allRows = (batchData?.data ?? []) as RawCA[];

  const rawByFundId: Record<number, RawCA | null> = {};
  selectedFundIds.forEach((fid) => {
    const rows = allRows.filter((r) => Number(r.fund_id) === fid);
    // When most_recent: backend used DISTINCT ON, rows has at most 1 entry (the best one)
    // When specific period: take the best-sorted row from API results
    rawByFundId[fid] =
      rows.length > 0
        ? ([...rows].sort(compareCapitalAccountRows)[0] ?? null)
        : null;
  });

  const n = selectedFundIds.length;
  // Each fund: 2 data cols. Between funds: 1 spacer col. Total after label: 3n-1
  const dataColSpan = n === 0 ? 2 : 3 * n - 1;

  return (
    <div className="flex flex-col gap-4">
      <R1FilterBar />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">
              Capital Account
            </h1>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <ScrollableX className="bg-white">
              <table
                className="text-xs border-collapse"
                style={{
                  width: `${260 + n * 400 + Math.max(0, n - 1) * 12}px`,
                }}
              >
                <thead>
                  {/* Fund name header row */}
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th
                      className="sticky left-0 z-20 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 border-r border-gray-200 whitespace-nowrap"
                      style={{ minWidth: 260 }}
                    >
                      Field
                    </th>
                    {selectedFundIds.map((fid, i) => {
                      const fund = funds.find(
                        (f) => (f.id ?? f.fund_id) === fid,
                      );
                      const name = fund?.fund_name ?? `Fund ${fid}`;
                      const isLastFund = i === selectedFundIds.length - 1;
                      return (
                        <React.Fragment key={fid}>
                          {i > 0 && (
                            <th
                              style={{ minWidth: 12, width: 12 }}
                              className="bg-white"
                            />
                          )}
                          <th
                            colSpan={2}
                            className={[
                              "px-4 py-2.5 text-center text-[11px] font-bold text-gray-700 border-l border-gray-200",
                              isLastFund ? "border-r border-gray-200" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            style={{ minWidth: 400 }}
                          >
                            {name}
                          </th>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  {/* TF / LP sub-header */}
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th
                      className="sticky left-0 z-20 bg-gray-50 border-r border-gray-200"
                      style={{ minWidth: 260 }}
                    />
                    {selectedFundIds.map((fid, i) => {
                      const isLastFund = i === selectedFundIds.length - 1;
                      return (
                        <React.Fragment key={fid}>
                          {i > 0 && (
                            <th
                              style={{ minWidth: 12, width: 12 }}
                              className="bg-white"
                            />
                          )}
                          <th
                            className="px-4 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 border-l border-gray-200"
                            style={{ minWidth: 200 }}
                          >
                            Total Fund (100%)
                          </th>
                          <th
                            className={[
                              "px-4 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 border-l border-gray-100",
                              isLastFund ? "border-r border-gray-200" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            style={{ minWidth: 200 }}
                          >
                            LP Share
                          </th>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {ROW_CONFIG.map((cfg, idx) => {
                    if (cfg.type === "section") {
                      return (
                        <tr key={idx} className="bg-gray-100">
                          {/* Empty sticky label cell */}
                          <td
                            className="sticky left-0 z-10 bg-gray-100 border-r border-gray-200 border-b border-b-gray-200"
                            style={{ minWidth: 260 }}
                          />
                          {/* Section label centered across all fund columns */}
                          <td
                            colSpan={dataColSpan}
                            className="border-b border-b-gray-200 border-r border-gray-200 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-widest text-gray-500"
                          >
                            {cfg.label}
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr
                        key={idx}
                        className="group border-t border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        {/* Field label */}
                        <td
                          className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-1.5 text-gray-700 border-r border-gray-200 whitespace-nowrap transition-colors"
                          style={{ minWidth: 260 }}
                        >
                          {cfg.label}
                        </td>

                        {selectedFundIds.map((fid, i) => {
                          const raw = rawByFundId[fid];
                          const tf = raw ? resolveValue(raw, cfg, "tf") : "—";
                          const lp = raw ? resolveValue(raw, cfg, "r1") : "—";
                          const isLastFund = i === selectedFundIds.length - 1;

                          return (
                            <React.Fragment key={`${fid}-${idx}`}>
                              {i > 0 && (
                                <td
                                  className="bg-white"
                                  style={{ minWidth: 12, width: 12 }}
                                />
                              )}
                              <td
                                className="px-4 py-1.5 text-right tabular-nums text-gray-700 border-l border-gray-200"
                                style={{ minWidth: 200 }}
                              >
                                {cfg.isSingle ? (
                                  <span className="block text-center font-medium">
                                    {tf}
                                  </span>
                                ) : (
                                  tf
                                )}
                              </td>
                              <td
                                className={[
                                  "px-4 py-1.5 text-right tabular-nums text-gray-700 border-l border-gray-100",
                                  isLastFund ? "border-r border-gray-200" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                style={{ minWidth: 200 }}
                              >
                                {cfg.isSingle ? null : lp}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollableX>
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildCapitalAccountFilteredUrl(
                  fundSelection,
                  selectedPeriod,
                )}
                filename={`r1_capital_account_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
